import { CatalogueManufacturer, CatalogueModel, EroundCatalogue, ModelMapping } from "../types/eroundProvisioning";
import { Prospect } from "../types/onboarding";

// Suggested eRound models for a wallbox eRound doesn't list (SOPS-187). Computed on the page and never stored:
// the mapping form's Save is the only write. Earlier stand-in decisions rank first, then eRound models whose
// connectors match what is known about the wallbox. eRound's model names are never read for this, only its
// connector data: some names contradict it.

export type Current = "AC" | "DC";
export type ConnectorKind = "socket" | "cable";

export interface WallboxHints {
  powerKw: number | null;
  connector: ConnectorKind | null;
  current: Current;
  chargingPoints: number;
}

export interface Suggestion {
  manufacturer: CatalogueManufacturer;
  model: CatalogueModel;
  score: number;
  reasons: string[];
  // Where a stand-in decision's target differs from what support entered.
  warnings: string[];
  // The stand-in decisions that point at this model.
  decisions: ModelMapping[];
}

const SUGGESTIONS = 3;

// Words that say nothing about which wallbox it is, so two names sharing only these are not similar.
const GENERIC = new Set([
  "charger",
  "charge",
  "charging",
  "wallbox",
  "box",
  "pro",
  "plus",
  "smart",
  "home",
  "wall",
  "ev",
  "ac",
  "dc",
  "station",
  "ladestation",
  "socket",
  "steckdose",
  "kabel",
  "cable",
  "festes",
  "typ",
  "type",
  "kw",
  "edition",
]);

const PREFIX: Record<ConnectorKind, string> = { socket: "S_TYPE_2", cable: "C_TYPE_2" };
const CONNECTOR_WORDS: Record<ConnectorKind, string> = { socket: "Type 2 socket", cable: "Type 2 cable" };

const normalise = (value: string): string => value.replace(/®/g, "").trim().replace(/\s+/g, " ").toLowerCase();

/** What a wallbox's name says about it: "Wattpilot Flex Pro 11kW" → 11 kW, AC. */
export const hintsFromName = (manufacturer: string, model: string): WallboxHints => {
  const text = `${manufacturer} ${model}`.toLowerCase();
  const powers = Array.from(text.matchAll(/(\d+(?:[.,]\d+)?)\s*kw/g), (m) => Number(m[1].replace(",", ".")));
  const points = text.match(/\b(\d)\s*x\s*\d/);
  return {
    powerKw: powers.length ? Math.max(...powers) : null,
    connector: /steckdose|socket|buchse/.test(text) ? "socket" : /kabel|cable/.test(text) ? "cable" : null,
    current: /\bdc\b|ccs|hpc/.test(text) ? "DC" : "AC",
    chargingPoints: points ? Number(points[1]) : 1,
  };
};

type ProspectSpecs = Partial<
  Pick<
    Prospect,
    "wallbox_other" | "wallbox_power_kw" | "wallbox_current_type" | "wallbox_connection_type" | "station_evse_counts"
  >
> &
  Pick<Prospect, "technical_names">;

/** What the customer entered for an unlisted wallbox (CWA-229), with the given station's charging points. */
export const hintsFromProspect = (prospect: ProspectSpecs, stationId: string): WallboxHints | null => {
  const { wallbox_power_kw: powerKw, wallbox_current_type: current, wallbox_connection_type: connection } = prospect;
  if (!prospect.wallbox_other || powerKw == null || !current || !connection) return null;
  const index = prospect.technical_names.indexOf(stationId);
  return {
    powerKw,
    connector: connection === "CABLE" ? "cable" : "socket",
    current,
    chargingPoints: (index >= 0 ? prospect.station_evse_counts?.[index] : undefined) ?? 1,
  };
};

const tokens = (name: string): Set<string> =>
  new Set(
    normalise(name)
      .split(/[^a-z0-9äöüß]+/)
      .filter((t) => t.length > 1 && !/\d/.test(t) && !GENERIC.has(t))
  );

/** Two wallbox names share most of their distinctive words: "Wattpilot Flex Pro 11" ~ "Fronius Wattpilot Flex". */
export const similarNames = (a: string, b: string): boolean => {
  const [x, y] = [tokens(a), tokens(b)];
  const [smaller, larger] = x.size <= y.size ? [x, y] : [y, x];
  if (smaller.size === 0) return false;
  const shared = Array.from(smaller).filter((t) => larger.has(t)).length;
  return shared * 2 >= smaller.size;
};

const day = (iso: string): string => {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
};

const kw = (value: number): string => `${Number(value.toFixed(1))} kW`;

const fitsCurrent = (model: CatalogueModel, current: Current): boolean =>
  current === "AC"
    ? model.connectors.every((c) => c.current === "AC")
    : model.connectors.some((c) => c.current === "DC");

const hasPower = (model: CatalogueModel, powerKw: number): boolean =>
  model.connectors.some((c) => c.maxPowerKw !== null && Math.abs(c.maxPowerKw - powerKw) < 0.1);

const allOfKind = (model: CatalogueModel, kind: ConnectorKind): boolean =>
  model.connectors.every((c) => (c.connectorType ?? "").startsWith(PREFIX[kind]));

const tested = (manufacturer: CatalogueManufacturer): boolean =>
  Boolean(manufacturer.ocpp16CoreTested && manufacturer.ocpp16SmartChargingTested);

/** The best eRound models for a wallbox, best first, each with the reasons it was chosen. */
export const suggestEroundModels = (
  wallbox: { manufacturer: string; model: string },
  hints: WallboxHints,
  catalogue: EroundCatalogue,
  mappings: ModelMapping[]
): Suggestion[] => {
  const ours = `${wallbox.manufacturer} ${wallbox.model}`;
  const standIns = mappings.filter((m) => m.standIn !== null);
  const found: Suggestion[] = [];

  catalogue.manufacturers.forEach((manufacturer) =>
    manufacturer.models.forEach((model) => {
      if (model.connectors.length === 0 || !fitsCurrent(model, hints.current)) return;
      const decisions = standIns.filter(
        (d) => d.eroundManufacturer === manufacturer.name && d.eroundModel === model.name
      );
      let score = 0;
      const reasons: string[] = [];
      const warnings: string[] = [];

      decisions.forEach((d) => {
        const what = `${d.onboardingManufacturer} ${d.onboardingModel}`;
        const saved = `saved by ${d.setBy}, ${day(d.setAt)}`;
        if (normalise(d.onboardingManufacturer) === normalise(wallbox.manufacturer)) {
          score += 40;
          reasons.push(`Stand-in for ${what} (${saved})`);
        } else if (similarNames(what, ours)) {
          score += 40;
          reasons.push(`Stand-in for similar ${what} (${saved})`);
        } else {
          score += 15;
          reasons.push(`Also the stand-in for ${what}`);
        }
      });
      const decided = decisions.length > 0;

      // A model nobody chose before has to match what is known; with no power known, only decisions count.
      if (hints.powerKw === null) {
        if (!decided) return;
      } else if (hasPower(model, hints.powerKw)) {
        score += 20;
        reasons.push(kw(hints.powerKw));
      } else if (decided) {
        const its = model.connectors.map((c) => (c.maxPowerKw === null ? "?" : kw(c.maxPowerKw)));
        warnings.push(`Note: ${Array.from(new Set(its)).join(", ")}, you entered ${kw(hints.powerKw)}`);
      } else {
        return;
      }
      if (hints.connector !== null) {
        if (allOfKind(model, hints.connector)) {
          score += 10;
          reasons.push(CONNECTOR_WORDS[hints.connector]);
        } else if (decided) {
          warnings.push(`Note: not a ${CONNECTOR_WORDS[hints.connector]}`);
        } else {
          return;
        }
      }

      const points = model.connectors.length;
      if (points === hints.chargingPoints) {
        score += 10;
        reasons.push(points === 1 ? "1 charging point" : `${points} charging points`);
      } else {
        score -= 10;
      }
      if (normalise(manufacturer.name) === normalise(wallbox.manufacturer)) {
        score += 20;
        reasons.push("Same manufacturer in eRound");
      }
      if (model.integrationStatus === "FULLY_INTEGRATED") {
        score += 10;
        reasons.push("Fully integrated");
      }
      if (tested(manufacturer)) {
        score += 5;
        reasons.push("OCPP 1.6 tested");
      }
      found.push({ manufacturer, model, score, reasons, warnings, decisions });
    })
  );

  const integrated = (s: Suggestion) => (s.model.integrationStatus === "FULLY_INTEGRATED" ? 1 : 0);
  return found
    .sort(
      (a, b) =>
        b.score - a.score ||
        integrated(b) - integrated(a) ||
        Number(tested(b.manufacturer)) - Number(tested(a.manufacturer)) ||
        a.manufacturer.name.localeCompare(b.manufacturer.name) ||
        a.model.name.localeCompare(b.model.name)
    )
    .slice(0, SUGGESTIONS);
};
